import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { ArrowRight, Award, MapPin, Calendar, Palette } from 'lucide-react';
import { artistInfo, artworks } from '../data/artworksData';

const AboutPage = () => {
  const { i18n } = useTranslation();
  const lang = i18n.language === 'ru' ? 'ru' : 'en';

  // Stats
  const totalWorks = artworks.length;
  const soldWorks = artworks.filter(a => a.status === 'sold').length;
  const collections = [...new Set(artworks.map(a => a.series))].length;

  const stats = [
    { value: totalWorks, label: { ru: 'Работ', en: 'Artworks' } },
    { value: soldWorks, label: { ru: 'В частных коллекциях', en: 'In Private Collections' } },
    { value: collections, label: { ru: 'Серий', en: 'Series' } },
    { value: '15+', label: { ru: 'Лет опыта', en: 'Years Experience' } }
  ];

  const timeline = [
    {
      year: '2009',
      title: { ru: 'Начало творческого пути', en: 'Beginning of Creative Journey' },
      description: { ru: 'Первые шаги в живописи, обучение основам академического рисунка', en: 'First steps in painting, learning the basics of academic drawing' }
    },
    {
      year: '2012',
      title: { ru: 'Член Союза художников', en: 'Member of Artists Union' },
      description: { ru: 'Принятие в Союз художников России', en: 'Admission to the Union of Artists of Russia' }
    },
    {
      year: '2015',
      title: { ru: 'Первая персональная выставка', en: 'First Solo Exhibition' },
      description: { ru: 'Персональная выставка в Центральном доме художника, Москва', en: 'Solo exhibition at the Central House of Artists, Moscow' }
    },
    {
      year: '2018',
      title: { ru: 'Международное признание', en: 'International Recognition' },
      description: { ru: 'Участие в международных выставках в Европе и США', en: 'Participation in international exhibitions in Europe and the USA' }
    },
    {
      year: '2020',
      title: { ru: 'Онлайн-галерея', en: 'Online Gallery' },
      description: { ru: 'Запуск персональной онлайн-галереи', en: 'Launch of personal online gallery' }
    },
    {
      year: '2024',
      title: { ru: 'Новая серия работ', en: 'New Series of Works' },
      description: { ru: 'Презентация новой серии "Игра света"', en: 'Presentation of the new series "Play of Light"' }
    }
  ];

  return (
    <div className="min-h-screen bg-gallery-bg pt-20">
      {/* Hero Section */}
      <section className="bg-white">
        <div className="container mx-auto px-4 py-16 md:py-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            {/* Artist Photo */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              className="relative"
            >
              <div className="aspect-[4/5] bg-gallery-light overflow-hidden">
                <img
                  src="/images/draw.jpeg"
                  alt={artistInfo.name[lang]}
                  className="w-full h-full object-cover"
                />
              </div>
              {/* Decorative element */}
              <div className="absolute -bottom-6 -right-6 w-32 h-32 border-2 border-gallery-accent -z-10" />
            </motion.div>

            {/* Info */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <p className="text-gallery-accent text-sm tracking-wider uppercase mb-4">
                {lang === 'ru' ? 'О художнике' : 'About the Artist'}
              </p>
              <h1 className="font-serif text-4xl md:text-5xl text-gallery-text mb-6">
                {artistInfo.name[lang]}
              </h1>

              <div className="flex items-center gap-2 text-gallery-muted mb-6">
                <MapPin size={18} />
                <span>{artistInfo.location[lang]}</span>
              </div>

              <p className="text-gallery-muted leading-relaxed mb-8">
                {artistInfo.bio[lang]}
              </p>

              <Link
                to="/gallery"
                className="inline-flex items-center gap-3 bg-gallery-text text-white px-6 py-3 text-sm tracking-wider uppercase hover:bg-gallery-accent transition-colors"
              >
                {lang === 'ru' ? 'Смотреть работы' : 'View Works'}
                <ArrowRight size={18} />
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Artist Statement */}
      <section className="py-20 md:py-28 bg-gallery-text text-white">
        <div className="container mx-auto px-4 max-w-4xl text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <Palette className="mx-auto mb-8 opacity-50" size={40} />
            <h2 className="font-serif text-2xl md:text-3xl mb-8">
              {lang === 'ru' ? 'Творческое кредо' : 'Artist Statement'}
            </h2>
            <blockquote className="font-serif text-xl md:text-2xl font-light leading-relaxed italic opacity-90">
              "{artistInfo.statement[lang]}"
            </blockquote>
          </motion.div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className="text-center"
              >
                <div className="font-serif text-4xl md:text-5xl text-gallery-accent mb-2">
                  {stat.value}
                </div>
                <div className="text-gallery-muted text-sm uppercase tracking-wider">
                  {stat.label[lang]}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="py-20 md:py-28 bg-gallery-bg">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="font-serif text-3xl md:text-4xl text-gallery-text mb-4">
              {lang === 'ru' ? 'Творческий путь' : 'Creative Journey'}
            </h2>
            <p className="text-gallery-muted max-w-xl mx-auto">
              {lang === 'ru'
                ? 'Ключевые этапы художественной карьеры'
                : 'Key milestones of the artistic career'
              }
            </p>
          </motion.div>

          <div className="max-w-3xl mx-auto">
            {timeline.map((item, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, x: idx % 2 === 0 ? -20 : 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className="relative pl-8 pb-12 border-l border-gallery-border last:pb-0"
              >
                {/* Timeline dot */}
                <div className="absolute left-0 top-0 -translate-x-1/2 w-4 h-4 bg-gallery-accent rounded-full" />

                <div className="flex items-center gap-3 mb-2">
                  <Calendar size={16} className="text-gallery-muted" />
                  <span className="text-gallery-accent font-medium">{item.year}</span>
                </div>
                <h3 className="font-serif text-xl text-gallery-text mb-2">
                  {item.title[lang]}
                </h3>
                <p className="text-gallery-muted">
                  {item.description[lang]}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Recognition */}
      <section className="py-20 md:py-28 bg-white">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="font-serif text-3xl md:text-4xl text-gallery-text mb-4">
              {lang === 'ru' ? 'Признание' : 'Recognition'}
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            {[
              {
                icon: Award,
                title: { ru: 'Член Союза художников', en: 'Artists Union Member' },
                description: { ru: 'Союз художников России с 2012 года', en: 'Union of Artists of Russia since 2012' }
              },
              {
                icon: MapPin,
                title: { ru: 'Международные выставки', en: 'International Exhibitions' },
                description: { ru: 'Выставки в России, Европе и США', en: 'Exhibitions in Russia, Europe and USA' }
              },
              {
                icon: Palette,
                title: { ru: 'Частные коллекции', en: 'Private Collections' },
                description: { ru: 'Работы в коллекциях по всему миру', en: 'Works in collections worldwide' }
              }
            ].map((item, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className="text-center p-8 bg-gallery-bg"
              >
                <item.icon className="mx-auto mb-4 text-gallery-accent" size={32} />
                <h3 className="font-serif text-xl text-gallery-text mb-2">
                  {item.title[lang]}
                </h3>
                <p className="text-gallery-muted text-sm">
                  {item.description[lang]}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gallery-bg">
        <div className="container mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-serif text-3xl md:text-4xl text-gallery-text mb-6">
              {lang === 'ru' ? 'Хотите приобрести работу?' : 'Interested in Acquiring a Work?'}
            </h2>
            <p className="text-gallery-muted max-w-xl mx-auto mb-8">
              {lang === 'ru'
                ? 'Свяжитесь со мной для получения подробной информации о доступных работах и условиях приобретения.'
                : 'Contact me for detailed information about available works and acquisition terms.'
              }
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact"
                className="inline-flex items-center justify-center gap-3 bg-gallery-text text-white px-8 py-4 text-sm tracking-wider uppercase hover:bg-gallery-accent transition-colors"
              >
                {lang === 'ru' ? 'Связаться' : 'Get in Touch'}
              </Link>
              <Link
                to="/gallery"
                className="inline-flex items-center justify-center gap-3 border border-gallery-border text-gallery-text px-8 py-4 text-sm tracking-wider uppercase hover:border-gallery-accent transition-colors"
              >
                {lang === 'ru' ? 'Смотреть галерею' : 'View Gallery'}
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
